import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Routes from '../configuration/routes/RouteList';
import {
  deletePersistedNote,
  listPersistedNotes,
  type PersistedTranscriptionNote as TranscriptionNote,
} from '../services/persistedNotes';

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

type SortKey = 'newest' | 'oldest' | 'title';

const Notes = () => {
  const [notes, setNotes] = useState<TranscriptionNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [sort, setSort] = useState<SortKey>('newest');

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      setNotes(await listPersistedNotes());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load notes');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleDelete = useCallback(async (id: string) => {
    if (!confirm('Delete this note?')) return;
    try {
      await deletePersistedNote(id);
      setNotes(current => current.filter(note => note.id !== id));
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Failed to delete note');
    }
  }, []);

  const folders = useMemo(() => {
    const set = new Set<string>();
    for (const n of notes) if (n.folder) set.add(n.folder);
    return Array.from(set).sort();
  }, [notes]);

  const filtered = useMemo(() => {
    let list = notes;
    if (activeFolder) list = list.filter(n => n.folder === activeFolder);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(n =>
        (n.title || '').toLowerCase().includes(q) || (n.text || '').toLowerCase().includes(q),
      );
    }
    const sorted = [...list];
    if (sort === 'newest') sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    else if (sort === 'oldest') sorted.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    else sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    return sorted;
  }, [notes, activeFolder, search, sort]);

  return (
    <>
      <title>NM | Notes</title>
      <div className="boxed-content" style={{ paddingTop: '28px' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600, letterSpacing: '-0.025em' }}>
              Transcripts
            </h2>
            {notes.length > 0 && !isLoading && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '11px',
                fontWeight: 500,
                padding: '1px 8px',
                borderRadius: '20px',
                background: 'var(--color-surface-3)',
                color: 'var(--color-muted-foreground)',
                border: '1px solid var(--color-border-subtle)',
              }}>
                {notes.length}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => void refresh()}
              className="btn-ghost"
              title="Refresh"
              style={{ fontSize: '16px', lineHeight: 1, padding: '6px 11px', borderRadius: '7px', cursor: 'pointer', border: '1px solid var(--color-border-subtle)' }}
            >
              ↻
            </button>
            <Link
              to={Routes.Upload}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '13px',
                fontWeight: 500,
                padding: '7px 16px',
                borderRadius: '7px',
                background: 'var(--color-primary)',
                color: 'var(--color-primary-foreground)',
                textDecoration: 'none',
              }}
            >
              + Upload Audio
            </Link>
          </div>
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{
            marginBottom: '1rem',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '13px',
            border: '1px solid var(--color-destructive)',
            color: 'var(--color-destructive)',
            background: 'oklch(from var(--color-destructive) l c h / 0.05)',
          }}>
            {error}
          </div>
        )}

        {/* ── Toolbar: search + folder tabs + sort ── */}
        {!isLoading && notes.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={{ position: 'relative', flex: '1 1 180px', minWidth: '160px', maxWidth: '280px' }}>
              <span style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '13px',
                color: 'var(--color-muted-foreground)',
                pointerEvents: 'none',
                opacity: 0.6,
              }}>
                ⌕
              </span>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search transcripts…"
                style={{
                  width: '100%',
                  paddingLeft: '30px',
                  paddingRight: '12px',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  fontSize: '13px',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border-subtle)',
                  background: 'var(--color-surface-1)',
                  color: 'var(--color-foreground)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Folder tabs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flex: '0 0 auto' }}>
              <button
                onClick={() => setActiveFolder(null)}
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: '5px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  border: '1px solid transparent',
                  background: activeFolder === null ? 'var(--color-surface-3)' : 'transparent',
                  color: activeFolder === null ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                  borderColor: activeFolder === null ? 'var(--color-border-subtle)' : 'transparent',
                }}
              >
                All
              </button>
              {folders.map(f => (
                <button
                  key={f}
                  onClick={() => setActiveFolder(activeFolder === f ? null : f)}
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    padding: '5px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: '1px solid transparent',
                    background: activeFolder === f ? 'var(--color-surface-3)' : 'transparent',
                    color: activeFolder === f ? 'var(--color-foreground)' : 'var(--color-muted-foreground)',
                    borderColor: activeFolder === f ? 'var(--color-border-subtle)' : 'transparent',
                  }}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Sort */}
            <div style={{ marginLeft: 'auto', flex: '0 0 auto' }}>
              <select
                value={sort}
                onChange={e => setSort(e.target.value as SortKey)}
                style={{ width: 'auto' }}
              >
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="title">Title A–Z</option>
              </select>
            </div>
          </div>
        )}

        {/* ── Loading skeleton ── */}
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
            {([0.9, 0.65, 0.4] as number[]).map((opacity, i) => (
              <div key={i} style={{
                height: '56px',
                borderRadius: i === 0 ? '8px 8px 2px 2px' : i === 2 ? '2px 2px 8px 8px' : '2px',
                background: 'var(--color-surface-1)',
                border: '1px solid var(--color-border-subtle)',
                opacity,
              }} />
            ))}
          </div>

        ) : notes.length === 0 ? (
          /* ── Empty state ── */
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '72px 32px',
            textAlign: 'center',
            borderRadius: '12px',
            border: '1px dashed var(--color-border)',
          }}>
            <div style={{
              width: '52px',
              height: '52px',
              borderRadius: '14px',
              background: 'var(--color-surface-2)',
              border: '1px solid var(--color-border-subtle)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              marginBottom: '18px',
            }}>
              🎙
            </div>
            <p style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 7px', letterSpacing: '-0.01em' }}>
              No transcripts yet
            </p>
            <p style={{
              fontSize: '12px',
              color: 'var(--color-muted-foreground)',
              margin: '0 0 24px',
              maxWidth: '240px',
              lineHeight: 1.65,
            }}>
              Upload an audio or video file to generate your first transcript
            </p>
            <Link
              to={Routes.Upload}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '12px',
                fontWeight: 500,
                padding: '7px 18px',
                borderRadius: '7px',
                background: 'var(--color-primary)',
                color: 'var(--color-primary-foreground)',
                textDecoration: 'none',
              }}
            >
              Upload Audio
            </Link>
          </div>

        ) : filtered.length === 0 ? (
          /* ── No search results ── */
          <div style={{
            padding: '48px 32px',
            textAlign: 'center',
            borderRadius: '10px',
            border: '1px dashed var(--color-border)',
          }}>
            <p style={{ fontSize: '13px', fontWeight: 500, margin: '0 0 6px' }}>No results</p>
            <p style={{ fontSize: '12px', color: 'var(--color-muted-foreground)', margin: 0 }}>
              Try a different search or filter
            </p>
          </div>

        ) : (
          /* ── List view ── */
          <>
            {/* Column header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              padding: '0 18px 6px',
              gap: '12px',
            }}>
              <span style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', opacity: 0.38, color: 'var(--color-muted-foreground)' }}>
                Transcript
              </span>
              <span style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', opacity: 0.38, color: 'var(--color-muted-foreground)' }}>
                Date
              </span>
            </div>
            <div
              className="section-panel"
              style={{ padding: 0, borderRadius: '10px', overflow: 'hidden' }}
            >
            {filtered.map((note, idx) => (
              <NoteRow
                key={note.id}
                note={note}
                onDelete={handleDelete}
                isLast={idx === filtered.length - 1}
              />
            ))}
          </div>
          </>
        )}
      </div>
    </>
  );
};

function NoteRow({
  note,
  onDelete,
  isLast,
}: {
  note: TranscriptionNote;
  onDelete: (id: string) => Promise<void>;
  isLast: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        gap: '12px',
        padding: '14px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border-subtle)',
        background: hovered ? 'var(--color-surface-3)' : 'transparent',
        transition: 'background 120ms ease',
        cursor: 'default',
      }}
    >
      {/* Left: title + meta */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
          <span style={{
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}>
            {note.title || 'Untitled'}
          </span>
          {note.folder && (
            <span style={{
              flexShrink: 0,
              fontSize: '10px',
              fontWeight: 500,
              padding: '1px 6px',
              borderRadius: '4px',
              background: 'var(--color-surface-3)',
              color: 'var(--color-muted-foreground)',
              border: '1px solid var(--color-border-subtle)',
              letterSpacing: '0.01em',
            }}>
              {note.folder}
            </span>
          )}
        </div>
        <p style={{
          fontSize: '12px',
          color: 'var(--color-muted-foreground)',
          margin: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          opacity: 0.75,
        }}>
          {note.text || <em style={{ opacity: 0.35, fontStyle: 'italic' }}>No content</em>}
        </p>
      </div>

      {/* Right: date + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <span style={{ fontSize: '12px', color: 'var(--color-muted-foreground)', opacity: 0.6, whiteSpace: 'nowrap' }}>
          {relativeTime(note.createdAt)}
        </span>
        <Link
          to={Routes.TranscriptionNote(note.id)}
          style={{
            fontSize: '12px',
            fontWeight: 500,
            color: 'var(--color-primary)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            opacity: hovered ? 1 : 0.65,
            transition: 'opacity 120ms ease',
          }}
        >
          Open →
        </Link>
        <button
          onClick={() => void onDelete(note.id)}
          title="Delete"
          style={{
            opacity: hovered ? 0.6 : 0,
            transition: 'opacity 120ms ease',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 7px',
            borderRadius: '5px',
            fontSize: '12px',
            color: 'var(--color-destructive)',
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}

export default Notes;
